import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import moment from 'moment-timezone';
import { toastr } from 'react-redux-toastr';
import { withTranslation } from 'react-i18next';

import { getPreferredTimezone } from 'common/helpers/time';
import { deriveSpectrumDisplay } from 'common/utils/spectrumDisplay';
import Selection from 'common/components/Chart/components/Selection';
import ChartItemContainer from 'common/components/Chart/components/ChartItemContainer';
import Loading from 'common/components/atoms/Loading';
import * as utils from 'common/utils';
import { OVERLAY_POINTS_LIMIT, getOverlayPointLimitExceededMessage } from 'common/constants';
import { ASSET_TYPE } from 'common/charts/constants';
import { chartFeatureDisplayName } from 'common/charts/chartI18n';
import { getFilteredPointsForDisplay, getFilteredPointsForDisplayByTrace } from 'utils/overlayUtils';
import { OVERLAY_TRACE_KEYS } from 'utils/overlayTraceUtils';
import UtilizationChart from '../../../Machines/MachineDetails/MachineCharts/components/UtilizationChart';
import TagChart from '../../../Machines/MachineDetails/MachineCharts/components/TagChart';
import WaterfallChart from '../../../Machines/MachineDetails/MachineCharts/components/molecules/WaterfallChart';
import NoDataChart from '../../../Machines/MachineDetails/MachineCharts/components/NoDataChart';
import ChartContainer from '../atoms/ChartContainer';
import assetHierarchyConstants from '../../constants/assetHierarchy.constants';
import { transformFromEnvelopeKeys, convertSpectrumDataToOrders } from '../../utils/envelopeUtils';
import { measureHasNoAlarmParameter, ownedThreshold } from '../../utils/alarmParameters';
import { SPECTRAL_ENVELOPE_KEYS } from '../../constants/envelope.constants';
import { getVisible as getAnnotationsVisible } from '../../../Annotations/selectors/annotations.selectors';
import OverlayTraceLegend from '../../../Machines/MachineDetails/MachineCharts/components/OverlayTraceLegend';
import ChartItemTagInfo, { GreenSpan } from '../molecules/ChartItemTagInfo';
import {
  getOverlayWaveformData,
  getOverlayChartColors,
  getOverlayChartActiveData,
} from '../../utils/chartItemOverlayUtils';

class ChartItem extends Component {
  constructor(props) {
    super(props);
    this.chartRef = React.createRef();
    this.assetType = this.getAssetType();
    // Identity-based cache for the derived spectrum display (see getSpectrumDisplay)
    this.spectrumDisplayCache = null;
    this.state = {
      errorBandpass: ''
    };
  }

  componentDidUpdate(prevProps) {
    // Handle chart type changes
    if (prevProps.chartType !== this.props.chartType) {
      this.setState({ errorBandpass: '' });
    }
  }

  /**
   * Derives everything the spectrum chart renders (data, units, features, overlay
   * points, envelope, speed markers) from pristine sources in a single place:
   * - `config.spectrum` / `config.spectrumFeatures` / `tag.forcingFrequencies` /
   *   overlay point `originalData`+`originalResponse` are the pristine source of truth.
   * - `props.spectrumXUnit` / `props.spectrumYUnit` are the *requested* display units
   *   owned by ChartsView (null = follow the backend/user-preference unit).
   * No converted spectrum state is ever stored, so data and units cannot drift apart.
   *
   * The result is cached by input identity so repeated renders are cheap and the
   * derived objects keep stable identities until an input actually changes.
   */
  getSpectrumDisplay = () => {
    const {
      config, tag, spectrumXUnit, spectrumYUnit, showOverlay,
      selectedPoints, chartType, feature, measurement_type, amp_type
    } = this.props;
    const spectrum = config ? config.spectrum : null;
    const configFeatures = config ? config.spectrumFeatures : null;
    const forcingFrequencies = tag ? tag.forcingFrequencies : null;
    const spectralEnvelopeSource = tag ? tag.spectral_envelope : null;

    const cacheKey = [
      spectrum, configFeatures, forcingFrequencies, spectralEnvelopeSource,
      measurement_type, amp_type, spectrumXUnit, spectrumYUnit,
      selectedPoints, showOverlay, chartType, feature
    ];
    const cache = this.spectrumDisplayCache;
    if (cache && cache.key.every((input, i) => input === cacheKey[i])) {
      return cache.value;
    }

    // Combine pristine features (config features + forcing frequencies). Each item
    // carries its own freq_units, which the derivation uses as the source unit.
    let features = null;
    if (configFeatures || forcingFrequencies) {
      const items = [];
      if (configFeatures && Array.isArray(configFeatures.items)) {
        items.push(...configFeatures.items);
      }
      if (forcingFrequencies && Array.isArray(forcingFrequencies.items)) {
        items.push(...forcingFrequencies.items);
      }
      features = { items };
    }

    const spectralEnvelope = chartType === 'spectrum' ? this.getEnvelopeData() : null;

    const display = deriveSpectrumDisplay({
      spectrum,
      features,
      requestedXUnit: spectrumXUnit,
      requestedYUnit: spectrumYUnit,
      tagType: tag ? tag.type : null,
      selectedPoints,
      showOverlay,
      spectralEnvelope,
      envelopeXUnit: this.getEnvelopeXUnit(spectralEnvelope)
    });

    // Narrowband filtering and overlay speed markers are part of the same derived view
    display.pointsForDisplay = getFilteredPointsForDisplay(display.selectedPoints, feature, showOverlay);
    display.multipleSpeedMarkers = this.computeMultipleSpeedMarkers(display);
    display.chartFeatures = this.computeChartFeatures(display);

    this.spectrumDisplayCache = { key: cacheKey, value: display };
    return display;
  }

  /**
   * Filters selected points for display based on the current chart mode (narrowband vs. broadband)
   * For narrowband views, it only returns points that have a valid speed.
   * Uses centralized filtering logic for consistency across all chart components.
   */
  getPointsForDisplay = () => this.getSpectrumDisplay().pointsForDisplay

  getPointsForTraceDisplay = (dataKey) => {
    const { feature, selectedPoints, showOverlay } = this.props;

    return getFilteredPointsForDisplayByTrace(selectedPoints, feature, showOverlay, dataKey);
  }

  getTraceDataKeyForChart = () => {
    const { chartType } = this.props;
    if (chartType === 'waveform') {
      return OVERLAY_TRACE_KEYS.WAVEFORM;
    }
    return OVERLAY_TRACE_KEYS.SPECTRUM;
  }

  /**
   * Computes data about multiple speeds for marker rendering (pure derivation).
   * Associates each speed with its point index for color matching.
   * Uses filtered points for narrowband consistency.
   */
  computeMultipleSpeedMarkers = (display) => {
    const { config, showOverlay, feature } = this.props;
    const currentXUnit = display.xUnit;

    // Reuse the filtered points from the same derivation; recompute only as a fallback.
    const filteredPoints = display.pointsForDisplay ||
      getFilteredPointsForDisplay(display.selectedPoints, feature, showOverlay);

    // Only process if overlay is enabled AND we have points
    if (!showOverlay || !Array.isArray(filteredPoints) || filteredPoints.length === 0) {
      return [];
    }

    // Extract valid speeds with their indices and colors
    return filteredPoints
      .map((point, index) => {
        // Get point's speed from various possible sources
        const speed = point.shaft_speed ||
          (point.spectrum && point.spectrum.shaft_speed) ||
          (config && config.spectrum && config.spectrum.shaft_speed) ||
          0;

        // For Orders mode, we need to store additional marker info
        const markerInfo = currentXUnit === 'Orders' ? {
          // Store markers for 1M, 2M, 3M, 2LF
          markers: [
            { name: '1M', order: 1 },
            { name: '2M', order: 2 },
            { name: '3M', order: 3 },
            { name: '2LF', order: 2 } // Line frequency harmonic
          ].map(marker => ({
            ...marker,
            // Calculate actual frequency for each marker based on this point's speed
            frequency: marker.order * speed,
            // Store the point's color for consistent rendering
            color: point.color,
            // Store pointIndex to ensure correct association with data point
            pointIndex: index
          }))
        } : null;

        return {
          index,
          speed,
          timestamp: point.x || point.timestamp,
          color: point.color,
          markerInfo,
          // Store original point to ensure precise reference
          originalPoint: point
        };
      })
      .filter(item => item.speed > 0); // Filter out invalid speeds
  }

  /**
   * Builds the features passed to the spectrum chart from the derived display
   * (already in display units), adjusting speed markers for overlay Orders mode.
   * @returns {Object} Features object with properly positioned markers
   */
  computeChartFeatures = (display) => {
    const { showOverlay } = this.props;
    const { multipleSpeedMarkers } = display;
    const spectrumXUnit = display.xUnit;

    // Start with derived features (config features + forcing frequencies, converted)
    const features = display.features ? _.cloneDeep(display.features) : { items: [] };
    if (!Array.isArray(features.items)) features.items = [];

    // Only adjust markers when overlay is actually enabled
    // This prevents stale overlay markers from appearing when overlay is disabled
    if (showOverlay && spectrumXUnit === 'Orders' && multipleSpeedMarkers && multipleSpeedMarkers.length > 0) {
      // Create a new array for the adjusted markers
      const adjustedMarkers = [];

      // Process each speed's markers
      multipleSpeedMarkers.forEach((speedData) => {
        if (speedData.markerInfo && speedData.markerInfo.markers) {
          speedData.markerInfo.markers.forEach((marker) => {
            // FIX: Include pointIndex from the marker to ensure correct point association
            const pointIndex = marker.pointIndex !== undefined ? marker.pointIndex : speedData.index;

            // For markers, use the point's actual shaft speed instead of marker.frequency
            const markerName = marker.name || marker.marker;
            const is1xMarker = markerName === '1M' || markerName === '1x' || markerName === '1X';
            const markerFrequency = is1xMarker ? speedData.speed : marker.frequency;

            adjustedMarkers.push({
              description: `${markerName} (${speedData.speed.toFixed(2)} Hz)`,
              frequency: markerFrequency,
              freq_units: 'Hz',
              marker: markerName,
              color: speedData.color, // Use the point's color for its markers
              // FIX: Store pointIndex to maintain connection to the original point
              pointIndex,
              // FIX: Attach reference to original point's timestamp for verification
              timestamp: speedData.timestamp,
              // FIX: Ensure we can track which point this marker belongs to
              originalPointIndex: speedData.index
            });
          });
        }
      });

      // Replace the standard markers with our adjusted ones
      features.items = features.items.filter(item =>
        !['1M', '2M', '3M', '2LF'].includes(item.marker)
      ).concat(adjustedMarkers);
    }

    return features;
  }

  /**
   * Unit changes are pure *requests* forwarded to ChartsView (the unit owner).
   * All data conversion happens in the derivation (getSpectrumDisplay), so the
   * chart always receives data and units that were produced together.
   */
  toggleSpecFrequencyUnits = (units) => {
    if (units && this.props.setSpectrumXUnit) {
      this.props.setSpectrumXUnit(units);
    }
  }

  toggleSpectrumAmplitudeUnits = (units) => {
    if (units && this.props.setSpectrumYUnit) {
      this.props.setSpectrumYUnit(units);
    }
  }

  getAssetType = () => {
    const { tag } = this.props;
    if (tag && tag.no_of_bins) return ASSET_TYPE.VFD;
    return ASSET_TYPE.FIXED_SPEED;
  }

  getEnvelopeData = () => {
    const { measurement_type, amp_type,
      tag: { spectral_envelope },
      config: { spectrum } } = this.props;

    let speed_bin = 0;
    if (spectrum) {
      speed_bin = spectrum.speed_bin;
    }

    const bin_no = `bin_${speed_bin || 0}`;
    if (spectral_envelope && spectral_envelope[measurement_type]) {
      const data = transformFromEnvelopeKeys(
        spectral_envelope[measurement_type],
        amp_type,
        SPECTRAL_ENVELOPE_KEYS
      );
      if (data) return data[bin_no];
    }
    return null;
  }

  isLoading = () => {
    const { config } = this.props;
    return (
      config.trends_data && config.trends_data.loading ||
      config.waterfall_spectrum && config.waterfall_spectrum.loading
    );
  }

  getNoDataTitle = () => {
    const { errorBandpass } = this.state;
    const {
      customDayFrom,
      customDayTo,
      chartType,
      amp_type,
      days,
      t
    } = this.props;

    let title;

    if (days === 'custom' && customDayFrom && customDayTo) {
      const from = customDayFrom.toString().slice(0, 15);
      const to = customDayTo.toString().slice(0, 15);
      title = t('machineDashboard.health.charts.noDataAvailableFromTo', { from, to });
    } else {
      title = t('machineDashboard.health.charts.noDataAvailable');
    }
    if (chartType === 'waterfall_spectrum') {
      title = t('assetHierarchy.chartItem.noData.waterfallSpectrum');
    }
    if (errorBandpass && amp_type === 'demod') {
      title = (
        <>
          {t('assetHierarchy.chartItem.noData.bandpassNotSetPrefix')}
          <GreenSpan onClick={() => this.props.openEditDemod()}>{t('common.edit')}</GreenSpan>
          {t('assetHierarchy.chartItem.noData.bandpassNotSetSuffix')}
        </>
      );
    }
    return title;
  }

  getTrendChartHeight = (chartType) => {
    switch (chartType) {
      case 'TSW':
        return '100px';
      case 'trend':
        return '300px';
      default:
        return '70px';
    }
  }

  getFilterSettings = (type) => {
    const { config } = this.props;
    if (!config.spectrum || this.props.amp_type !== 'demod') return null;
    if (!config.spectrum.fmax) return null;
    const settings = {};
    settings.units = this.props.currentUser.frequency_units;
    switch (type) {
      case 'demod_spectrum':
        settings.filterTitle = this.props.t('assetHierarchy.chartItem.filters.spectrum');
        settings.lowerFilter = this.props.tag.bandpass_lower_cutoff || assetHierarchyConstants.DEMOD_TREND_LOWER_FILTER;
        settings.upperFilter = this.props.tag.bandpass_upper_cutoff;
        return settings;

      case 'demod_waveform':
        settings.filterTitle = this.props.t('assetHierarchy.chartItem.filters.waveform');
        settings.lowerFilter = this.props.tag.bandpass_lower_cutoff || assetHierarchyConstants.DEMOD_TREND_LOWER_FILTER;
        settings.upperFilter = this.props.tag.bandpass_upper_cutoff;
        return settings;

      case 'demod_trend':
        settings.filterTitle = this.props.t('assetHierarchy.chartItem.filters.trend');
        settings.lowerFilter = assetHierarchyConstants.DEMOD_TREND_LOWER_FILTER * utils.getFreqConversionFactor('Hz', settings.units);
        settings.upperFilter = config.spectrum.fmax;
        return settings;

      default:
        return null;
    }
  }

  getAlarmThreshold = (type) => {
    const { tag, bin_nos, feature, amp_type } = this.props;
    const thresholds = tag.alarm_thresholds;
    if (!thresholds) return null;

    // Only the nesting differs by tag type: vibration keeps its limits under
    // speed bins, every other tag type keeps them flat. Which key a measure
    // stores under is the same question for both, and is asked once below.
    let scope = thresholds;
    if (tag.type === 'vibration') {
      if (bin_nos.length > 1 || (amp_type !== 'velocity' && amp_type !== 'demod' && amp_type !== 'acceleration')) return null;
      const bin = `bin_${bin_nos.length === 0 ? 0 : bin_nos[0]}`;
      scope = thresholds[bin];
      if (!scope) return null;
    }

    // A measure that has its own alarm parameter owns its line.
    const owned = ownedThreshold(tag, amp_type, feature, type, scope);
    if (owned.owns) return owned.value;

    // No alarm parameter for this measure, so no line: the derived one it used to
    // draw - the amplitude's RMS limit scaled by √2 for peak, 2 for
    // peak-to-peak - is a limit nothing evaluates. this.noAlarmParameterHere() puts
    // the reason on screen in its place.
    if (measureHasNoAlarmParameter(tag, amp_type, feature)) return null;

    // A flat tag type with no metadata row for this measure keeps the line it
    // has always drawn. No factor is applied here, and never has been.
    if (tag.type !== 'vibration') return scope[type];

    const legacyKey = amp_type === 'velocity' ? type : `${amp_type}_${type}`;

    return scope[legacyKey] * utils.getMultiplyFactorForAlarm(feature);
  }

  getEnvelopeXUnit = envelope => (
    envelope && envelope.envelope_data && envelope.envelope_data.x_units
  )

  getOnOffReadingIndicator = () => {
    const { config } = this.props;
    const data = config.trends_data[0].trend_data[0];
    const isEnabled = typeof data === 'object' && Object.prototype.hasOwnProperty.call(data, 'on_off_reading');
    return isEnabled;
  }

  handlePointClick = (point, event) => {
    const { showOverlay, selectedPoints } = this.props;

    if (!showOverlay) {
      // Single selection mode
      this.props.getWaveAndSpecForId(point);
      return;
    }

    // Multi-selection mode
    if (event && (event.ctrlKey || event.metaKey)) {
      const isSelected = selectedPoints.some(p =>
        p.x && point.x && new Date(p.x).getTime() === new Date(point.x).getTime()
      );

      let newPoints;
      if (isSelected) {
        // Remove the point if already selected
        newPoints = selectedPoints.filter(p =>
          !(p.x && point.x && new Date(p.x).getTime() === new Date(point.x).getTime())
        );
      } else {
        // Check if we already have the maximum number of points selected
        if (selectedPoints.length >= OVERLAY_POINTS_LIMIT) {
          toastr.warning(this.props.t('charts.overlay.pointSelectionLimitTitle', {
            defaultValue: 'Point Selection Limit'
          }), getOverlayPointLimitExceededMessage(this.props.t));
          return;
        }

        // Add the point if not already selected and limit not reached.
        // Include a default description but let the parent component assign colors.
        // No unit metadata is stored here: fetches store pristine data and the
        // display units are applied during derivation.
        newPoints = [...selectedPoints, { ...point, description: '' }];
      }

      this.props.updateSelectedPoints(newPoints);

      // Fetch spectrum data for the clicked point (pristine data is stored by the parent)
      if (this.props.getWaveAndSpecForId) {
        this.props.getWaveAndSpecForId(point);
      }
    }
  }

  removeSelectedPoint = (pointToRemove) => {
    const { selectedPoints } = this.props;
    const newPoints = selectedPoints.filter(point => point.x !== pointToRemove.x);
    this.props.updateSelectedPoints(newPoints);
  }

  /**
   * Gets colors for chart rendering based on overlay state and selected points
   * Applies narrowband filtering for consistency across all chart components
   * @param {boolean} requiresSpectrumData - Whether to filter points by spectrum_data presence
   * @returns {Array} Array of color strings
   */
  getChartColors = (requiresTraceData = false, dataKey = OVERLAY_TRACE_KEYS.SPECTRUM) => {
    const { showOverlay, config } = this.props;
    const displayPoints = requiresTraceData
      ? this.getPointsForTraceDisplay(dataKey)
      : this.getPointsForDisplay();

    return getOverlayChartColors({
      showOverlay,
      defaultColor: config.trends_config[0].color,
      displayPoints,
      requiresTraceData,
      dataKey
    });
  }

  /**
   * Gets active data state for chart rendering
   * Applies narrowband filtering for consistency across all chart components
   * @param {boolean} requiresTraceData - Whether to filter points by trace data presence
   * @param {string} dataKey - spectrum_data or waveform_data
   * @returns {Array} Array of boolean values indicating active state
   */
  getChartActiveData = (requiresTraceData = false, dataKey = OVERLAY_TRACE_KEYS.SPECTRUM) => {
    const { showOverlay } = this.props;
    const displayPoints = requiresTraceData
      ? this.getPointsForTraceDisplay(dataKey)
      : this.getPointsForDisplay();

    return getOverlayChartActiveData({
      showOverlay,
      displayPoints,
      requiresTraceData,
      dataKey
    });
  }

  getSpectrumData = (display) => {
    const { showOverlay } = this.props;
    const resolvedDisplay = display || this.getSpectrumDisplay();
    // Use filtered points that exclude zero-speed points in narrowband mode
    const selectedPoints = resolvedDisplay.pointsForDisplay;

    if (showOverlay && selectedPoints && selectedPoints.length > 0) {
      // Get points with spectrum data from the already-filtered list
      const validPoints = selectedPoints.filter(point => point.spectrum_data);
      if (validPoints.length > 0) {
        // Return array of spectrum_data arrays
        return validPoints.map(point => point.spectrum_data);
      }
    }
    // Default case - just return the derived spectrum data
    return resolvedDisplay.spectrumData;
  }

  getWaveformData = () => {
    const { showOverlay, config } = this.props;

    return getOverlayWaveformData({
      showOverlay,
      displayPoints: this.getPointsForTraceDisplay(OVERLAY_TRACE_KEYS.WAVEFORM),
      fallbackWaveformData: config.waveform && config.waveform.waveform_data
    });
  }

  render() {
    const {
      amp_type,
      config,
      feature,
      tag,
      chartType,
      componentNode,
      currentUser,
      showOverlay,
      selectedPoints,
    } = this.props;

    if (!config) {
      return null;
    }

    // Single derivation of the spectrum display state (data, units, features,
    // overlay points, envelope) — memoized, see getSpectrumDisplay.
    const spectrumDisplay = this.getSpectrumDisplay();

    let hasData = false;
    if (
      config.trends_data &&
      config.trends_data[0] &&
      config.trends_data[0].trend_data &&
      config.trends_data[0].trend_data.length > 0
    ) {
      hasData = true;
    }
    const noData = (config.trends_data && !config.trends_data.loading && !hasData && chartType !== 'waterfall_spectrum') || (
      chartType === 'waterfall_spectrum' && config.waterfall_spectrum &&
      !config.waterfall_spectrum.loading && !config.waterfall_spectrum.items
    );
    const commonProps = {
      unit_system: this.props.currentUser.unit_system,
      addingNote: this.props.addingNote,
      addNote: this.props.addNote,
      config_id: config.config_id,
      tag_type: tag.type
    };

    const noDataChart = (
      <NoDataChart
        tagType={tag.type}
        configId={config.config_id}
        height="300px"
        days={this.props.days}
        title={this.getNoDataTitle()}
        domain={config.time_range}
      />
    );

    // Alarm lines are drawn on the trend only.
    const showsTrend = chartType === 'trend' || chartType === 'TSW';

    return (
      <ChartItemContainer innerRef={this.chartRef}>
        {!noData && (
          <ChartItemTagInfo
            chartType={chartType}
            config={config}
            tag={tag}
            noAlarmParameter={showsTrend && measureHasNoAlarmParameter(tag, amp_type, feature)}
            showOverlay={showOverlay}
            waveAndSpecSelection={this.props.waveAndSpecSelection}
          />
        )}
        <ChartContainer id="Chart-Container" minimizeHeight={this.props.minimizeHeight && false}>
          {this.isLoading() && <Loading />}
          {hasData && (
            <Fragment>
              {tag.type === 'utilization' && (
                <>
                  {this.props.selection && <Selection data={this.props.selection} annotationsVisible={this.props.annotationsVisible} />}
                  <UtilizationChart
                    trend={config.trends_data[0]}
                    type={tag.type}
                    expanded
                    calendarExpanded={this.props.calendarExpanded}
                    tagId={config.trends_data[0].tag_id}
                    selectedRange={this.props.selectedRange}
                    selection={this.props.selection}
                    mouseoverChart={this.props.mouseoverChart}
                    domain={config.time_range}
                    {...commonProps}
                  />
                </>
              )}
              {config.trends_data[0].tag_type !== 'utilization' && this.props.chartType !== 'waterfall_spectrum' && (
                <>
                  {this.props.selection && <Selection toFixedDecimalPoints={amp_type === 'demod' ? null : 2} data={this.props.selection} filterSettings={this.getFilterSettings('demod_trend')} annotationsVisible={this.props.annotationsVisible} />}

                  <TagChart
                    type={amp_type === 'demod' ? 'demod_trend' : 'trend'}
                    title={this.props.t('assetHierarchy.chartItem.titles.trend', { defaultValue: 'trend' })}
                    chartName={`configId-${config.config_id}-trend-analyst`}
                    data={config.trends_data[0].trend_data}
                    color={config.trends_config[0].color}
                    loading={config.trends_data.loading}
                    tagId={config.trends_data[0].tag_id}
                    expanded
                    yTitle={tag.structure_type === 'vector' ? (chartFeatureDisplayName(this.props.t, feature) || utils.CapitalizeOnlyFirstLetter(feature)) : utils.CapitalizeEachWord(config.trends_data[0].tag_type)}
                    yUnit={config.trends_data[0].units}
                    xIsDate
                    mouseoverChart={this.props.mouseoverChart}
                    selectedRange={this.props.selectedRange}
                    mouseClick={this.handlePointClick}
                    points={this.props.waveAndSpecSelection}
                    refreshChartAfterOutlier={this.props.refreshChartAfterOutlier}
                    storeBrushSelection={this.props.storeBrushSelection}
                    brushSelected={this.props.trendBrushSelection}
                    warningThreshold={this.getAlarmThreshold('warning')}
                    criticalThreshold={this.getAlarmThreshold('critical')}
                    baselineValue={config.trends_data[0].baseline_value}
                    ampType={this.props.amp_type}
                    feature={this.props.feature}
                    waveAndSpecSelection={this.props.waveAndSpecSelection}
                    setLastHoveredChart={this.props.setLastHoveredChart}
                    lastHoveredChart={this.props.lastHoveredChart}
                    height={this.getTrendChartHeight(this.props.chartType)}
                    contextHeight="25px"
                    miniMode={this.props.chartType !== 'trend' && this.props.chartType !== 'TSW'}
                    chart_size={this.props.chartType === 'trend' ? 'Large' : 'Normal'}
                    enableOnOffIndicator={this.getOnOffReadingIndicator()}
                    filterMinVal={this.props.filterMinVal}
                    filterMaxVal={this.props.filterMaxVal}
                    filterKey="speed"
                    paginationLoading={this.props.paginationLoading}
                    showBaseline={this.props.showBaseline}
                    editRULSettings={this.props.editRULSettings}
                    closeRULSettings={this.props.closeRULSettings}
                    rulFdp={tag.rul_settings && tag.rul_settings.fdp}
                    rulStartTime={tag.rul_settings && tag.rul_settings.start_time}
                    domain={config.time_range}
                    showOverlay={showOverlay}
                    selectedPoints={selectedPoints}
                    {...commonProps}
                    colors={this.getChartColors(false)}
                    activeData={this.getChartActiveData(false)}
                  />
                </>
              )}
              {config.spectrum &&
                (this.props.chartType === 'spectrum' || this.props.chartType === 'TSW') && (
                  <Fragment>
                    <TagChart
                      fmax={config.spectrum.fmax}
                      lines_of_resolution={config.spectrum.lines_of_resolution}
                      type={amp_type === 'demod' ? 'demod_spectrum' : 'spectrum'}
                      title={this.props.t('assetHierarchy.chartItem.titles.spectrum', { defaultValue: 'spectrum' })}
                      chartName={`configId-${config.config_id}-spectrum-analyst`}
                      data={this.getSpectrumData(spectrumDisplay)}
                      showOverlay={showOverlay}
                      selectedPoints={spectrumDisplay.selectedPoints}
                      multipleSpeedMarkers={spectrumDisplay.multipleSpeedMarkers}
                      updateSelectedPoints={this.props.updateSelectedPoints}
                      retryOverlayTrace={this.props.retryOverlayTrace}
                      color={config.trends_config[0].color}
                      loading={config.spectrum.loading}
                      originalData={config.spectrum.spectrum_data}
                      tagId={tag.id}
                      expanded
                      yTitle={this.props.t('common.charts.axis.amplitude', { defaultValue: 'Amplitude' })}
                      initialYunit={config.spectrum.amp_units}
                      yUnit={spectrumDisplay.yUnit}
                      xTitle={this.props.t('common.charts.axis.frequency', { defaultValue: 'Frequency' })}
                      xUnit={spectrumDisplay.xUnit}
                      initialXunit={config.spectrum.freq_units}
                      controlledUnits
                      tagType={tag.type}
                      toggleSpectrumFrequencyUnits={this.toggleSpecFrequencyUnits}
                      toggleSpectrumAmplitudeUnits={this.toggleSpectrumAmplitudeUnits}
                      xIsDate={false}
                      shaftSpeed={config.spectrum.shaft_speed}
                      xAxisType="linear"
                      ampType={this.props.amp_type}
                      feature={this.props.feature}
                      onFeatureDetailsClick={this.props.onFeatureDetailsClick}
                      onFeatureEditClick={this.props.onFeatureEditClick}
                      features={spectrumDisplay.chartFeatures}
                      filterSettings={this.getFilterSettings('demod_spectrum')}
                      setLastHoveredChart={this.props.setLastHoveredChart}
                      lastHoveredChart={this.props.lastHoveredChart}
                      storeBrushSelection={this.props.storeBrushSelection}
                      measurementTimestamp={this.props.waveAndSpecSelection && this.props.waveAndSpecSelection.x}
                      componentNode={componentNode}
                      config={config}
                      {...commonProps}
                      height={this.props.chartType === 'TSW' ? '100px' : '300px'}
                      contextHeight="25px"
                      chart_size={this.props.chartType === 'spectrum' ? 'Large' : 'Normal'}
                      spectralEnvelope={spectrumDisplay.spectralEnvelope}
                      envelopeXUnit={spectrumDisplay.xUnit}
                      shaftSpeedUnit={
                        config.spectrum.freq_units
                        || (currentUser && currentUser.frequency_units)
                        || 'Hz'
                      }
                      assetType={this.assetType}
                      colors={this.getChartColors(true)}
                      activeData={this.getChartActiveData(true)}
                    />
                  </Fragment>
              )}
              {config.waveform &&
                (this.props.chartType === 'waveform' || this.props.chartType === 'TSW') && (
                  <Fragment>
                    <TagChart
                      type="waveform"
                      title={this.props.t('assetHierarchy.chartItem.titles.waveform', { defaultValue: 'waveform' })}
                      chartName={`configId-${config.config_id}-waveform-analyst`}
                      data={this.getWaveformData()}
                      showOverlay={showOverlay}
                      selectedPoints={selectedPoints}
                      updateSelectedPoints={this.props.updateSelectedPoints}
                      retryOverlayTrace={this.props.retryOverlayTrace}
                      color={config.trends_config[0].color}
                      loading={config.waveform.loading}
                      tagId={tag.id}
                      expanded
                      yTitle={this.props.t('common.charts.axis.amplitude', { defaultValue: 'Amplitude' })}
                      yUnit={config.waveform.amp_units}
                      xTitle={this.props.t('common.charts.axis.time', { defaultValue: 'Time' })}
                      xUnit="ms"
                      ampType={this.props.amp_type}
                      xIsDate={false}
                      xAxisType="linear"
                      setLastHoveredChart={this.props.setLastHoveredChart}
                      lastHoveredChart={this.props.lastHoveredChart}
                      storeBrushSelection={this.props.storeBrushSelection}
                      {...commonProps}
                      height={this.props.chartType === 'TSW' ? '100px' : '300px'}
                      contextHeight="25px"
                      chart_size={this.props.chartType === 'waveform' ? 'Large' : 'Normal'}
                      filterSettings={this.getFilterSettings('demod_waveform')}
                      colors={this.getChartColors(true, OVERLAY_TRACE_KEYS.WAVEFORM)}
                      activeData={this.getChartActiveData(true, OVERLAY_TRACE_KEYS.WAVEFORM)}
                    />
                  </Fragment>
              )}
              {config.cepstrum && this.props.chartType === 'cepstrum' && (
                <Fragment>
                  <TagChart
                    type="waveform"
                    isCepstrum
                    title={this.props.t('assetHierarchy.chartItem.titles.cepstrum', { defaultValue: 'Cepstrum' })}
                    chartName={`configId-${config.config_id}-cepstrum-analyst`}
                    data={config.cepstrum.cepstrum_data}
                    color={config.trends_config[0].color}
                    loading={config.cepstrum.loading}
                    tagId={tag.id}
                    expanded
                    yTitle={this.props.t('common.charts.axis.amplitude', { defaultValue: 'Amplitude' })}
                    yUnit={config.cepstrum.amp_units}
                    xTitle={this.props.t('assetHierarchy.chartItem.axes.quefrency', { defaultValue: 'Quefrency' })}
                    xUnit="ms"
                    ampType={this.props.amp_type}
                    xIsDate={false}
                    xAxisType="linear"
                    setLastHoveredChart={this.props.setLastHoveredChart}
                    lastHoveredChart={this.props.lastHoveredChart}
                    storeBrushSelection={this.props.storeBrushSelection}
                    {...commonProps}
                    height="300px"
                    contextHeight="25px"
                    chart_size="Large"
                    filterSettings={this.getFilterSettings('demod_waveform')}
                  />
                </Fragment>
              )}
              {this.props.chartType === 'waterfall_spectrum' && tag && config.waterfall_spectrum && config.waterfall_spectrum.items && (() => {
                const items = config.waterfall_spectrum.items;
                const hasValidShaftSpeeds = items.every(sp => sp.shaft_speed != null && sp.shaft_speed > 0.001);
                const useOrders = this.props.waterfallXUnit === 'Orders' && hasValidShaftSpeeds;
                const spectrums = useOrders ? convertSpectrumDataToOrders(_.cloneDeep(items)) : items;
                return (
                  <WaterfallChart
                    spectrums={spectrums}
                    xUnit={useOrders ? 'Orders' : config.waterfall_spectrum.freq_units}
                    yUnit={config.waterfall_spectrum.amp_units}
                    yLabel={commonProps.tag_type === 'vibration' ? `Amplitude(${config.waterfall_spectrum.amp_units},0-Pk)` : `Amplitude(${config.waterfall_spectrum.amp_units})`}
                    hierarchyViewPane={this.props.hierarchyViewPane}
                    showPerspectiveChanger
                    showAmplitudeTagChart
                    showXUnitToggle={this.props.isVFD}
                    onXUnitToggle={this.props.toggleWaterfallXUnit}
                    disableXUnitToggle={!hasValidShaftSpeeds}
                  />
                );
              })()}
            </Fragment>
          )}
          {(noData || this.state.errorBandpass) && noDataChart}
        </ChartContainer>
        {showOverlay && chartType === 'trend' && (() => {
          // The Trend view shares the same overlay-legend cards as the Spectrum and
          // Waveform charts (OverlayTraceLegend). The selected trend points aren't
          // trace overlays, but the editable card layout lives on the isTraceOverlay
          // branch, so we drive it with the points themselves as legendPoints. Passing
          // the raw selectedPoints (not a cloned display list) keeps object identity
          // intact so description edits and removals resolve to the right point and save.
          const legendPoints = (selectedPoints || []).filter(point => point && !_.isEmpty(point));
          if (legendPoints.length === 0) return null;
          const timezone = getPreferredTimezone(currentUser, this.props.currentAccount)
            || moment.tz.guess();
          return (
            <OverlayTraceLegend
              isTraceOverlay
              showOverlay={showOverlay}
              timezone={timezone}
              selectedPoints={selectedPoints}
              legendPoints={legendPoints}
              validPointsForLegend={legendPoints}
              updateSelectedPoints={this.props.updateSelectedPoints}
              type={amp_type === 'demod' ? 'demod_trend' : 'trend'}
              config={config}
              multipleSpeedMarkers={spectrumDisplay.multipleSpeedMarkers}
              assetType={this.assetType}
            />
          );
        })()}
      </ChartItemContainer>
    );
  }
}

ChartItem.propTypes = {
  config: PropTypes.object.isRequired,
  currentAccount: PropTypes.object,
  showOverlay: PropTypes.bool,
  selectedPoints: PropTypes.array,
  updateSelectedPoints: PropTypes.func,
  retryOverlayTrace: PropTypes.func,
  // Requested display units, owned by ChartsView (null = follow backend/user preference)
  spectrumXUnit: PropTypes.string,
  spectrumYUnit: PropTypes.string,
  setSpectrumXUnit: PropTypes.func,
  setSpectrumYUnit: PropTypes.func,
  tag: PropTypes.object,
  chartType: PropTypes.string,
  getWaveAndSpecForId: PropTypes.func,
  waterfallXUnit: PropTypes.string,
  toggleWaterfallXUnit: PropTypes.func,
  isVFD: PropTypes.bool,
};

ChartItem.defaultProps = {
  currentAccount: {},
  showOverlay: false,
  selectedPoints: [],
  updateSelectedPoints: () => { },
  retryOverlayTrace: () => { },
  spectrumXUnit: null,
  spectrumYUnit: null,
  setSpectrumXUnit: () => { },
  setSpectrumYUnit: () => { },
  tag: {},
  chartType: '',
  getWaveAndSpecForId: () => { },
  waterfallXUnit: 'Hz',
  toggleWaterfallXUnit: () => { },
  isVFD: false,
};

const mapStateToProps = state => ({
  currentUser: state.user.user,
  currentAccount: state.currentAccount,
  rbacPermissions: state.rbacPermissions,
  hierarchyViewPane: state.hierarchyViewPane,
  componentId: state.breadcrumb.component.id,
  annotationsVisible: getAnnotationsVisible(state)
});

export default connect(mapStateToProps, {})(withRouter(withTranslation()(ChartItem)));
